// SuburbIntelligenceSection.tsx — HTAG Intelligence full rebuild (Day 28)
// Fetches LGA-level market data from htag-data edge function.
// Renders: PropertyMapPanel, AI summary, RCS scores, growth chart, demand/supply, fundamentals/risk, AVM.

import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import type { PropertyRecord } from '../../types/portfolio'
import type { HtagSummary, PropertyFinancialContext } from '../../lib/htag-types'
import { getPropertyTypeConfig, getHtagType } from '../../lib/htag-property-type'
import InfoTooltip from './InfoTooltip'
import PropertyMapPanel from './PropertyMapPanel'
import IntelligenceChart from './IntelligenceChart'
import MarketInsightSummary from './MarketInsightSummary'

interface SuburbIntelligenceSectionProps {
  property: PropertyRecord
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdate?: (updated: any) => void
  /** When provided, skip internal fetch — data is managed by parent */
  htagData?: HtagSummary | null
  htagLoading?: boolean
  htagError?: string | null
}

// ─── Formatting helpers ────────────────────────────────────────────────────────

const fmt = {
  pct: (v: number) => `${(v * 100).toFixed(1)}%`,
  pctInt: (v: number) => `${Math.round(v * 100)}%`,
  yr: (v: number) => `${v.toFixed(1)} yrs`,
  km: (v: number) => `${v.toFixed(0)} km`,
  currency: (v: number) => `$${v.toLocaleString('en-AU')}`,
  date: (s: string) => {
    const d = new Date(s)
    return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })
  },
  cagr: (v: number) => {
    const val = (v * 100).toFixed(1)
    return v >= 0 ? `+${val}%` : `${val}%`
  },
}

// ─── Tag helpers ──────────────────────────────────────────────────────────────

type TagColor = 'green' | 'amber' | 'red' | 'neutral'
interface TagConfig { label: string; color: TagColor }

const tagCx: Record<TagColor, string> = {
  green:   'bg-green-50 text-green-800',
  amber:   'bg-amber-50 text-amber-800',
  red:     'bg-red-50 text-red-800',
  neutral: 'bg-neutral-100 text-neutral-600',
}

function Tag({ label, color }: TagConfig) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tagCx[color]}`}>
      {label}
    </span>
  )
}

function domTag(dom: number): TagConfig {
  if (dom < 30) return { label: 'High demand', color: 'green' }
  if (dom < 60) return { label: 'Balanced', color: 'neutral' }
  return { label: 'Soft demand', color: 'red' }
}
function vacancyTag(v: number): TagConfig {
  if (v < 0.02) return { label: 'Very tight', color: 'green' }
  if (v < 0.035) return { label: 'Balanced', color: 'neutral' }
  return { label: 'Watch', color: 'amber' }
}
function inventoryTag(months: number): TagConfig {
  if (months < 2) return { label: "Seller's market", color: 'green' }
  if (months < 4) return { label: 'Balanced', color: 'neutral' }
  return { label: "Buyer's market", color: 'red' }
}
function riskTag(percentile: number): TagConfig {
  if (percentile >= 80) return { label: 'Low risk', color: 'green' }
  if (percentile >= 50) return { label: 'Moderate', color: 'amber' }
  return { label: 'Elevated', color: 'red' }
}
function cagrColor(v: number): string {
  if (v > 0.05) return 'text-green-700'
  if (v > 0) return 'text-green-600'
  return 'text-red-600'
}

function getCycleExplanation(phase: string): string {
  const p = phase.toLowerCase()
  if (p.includes('bottom')) return 'Prices have bottomed out — historically a good entry point.'
  if (p.includes('rising')) return 'Prices are accelerating upward — strong growth momentum.'
  if (p.includes('peak') && p.includes('+')) return 'Prices near the top — growth is slowing, proceed with caution.'
  if (p.includes('peak')) return 'Prices at peak — limited near-term upside expected.'
  if (p.includes('falling') || p.includes('decreasing')) return 'Prices declining — consider waiting for the bottom.'
  return 'Market cycle position currently being assessed.'
}

// ─── RCS plain-English interpretation ────────────────────────────────────────

function rcsOverallLabel(v: number): string {
  if (v >= 75) return 'strong market conditions'
  if (v >= 60) return 'good market conditions'
  if (v >= 45) return 'average market conditions'
  if (v >= 30) return 'below-average market conditions'
  return 'weak market conditions'
}

// ─── Score bar helpers ─────────────────────────────────────────────────────────

function scoreColor(v: number): string {
  if (v >= 60) return '#16A34A'
  if (v >= 40) return '#D97706'
  return '#DC2626'
}
function scoreBarColor(v: number): string {
  if (v >= 60) return '#16A34A'
  if (v >= 40) return '#C8761A'
  return '#DC2626'
}

// ─── Score bar row ────────────────────────────────────────────────────────────

function ScoreBar({ label, value, glossaryKey, accent }: {
  label: string
  value: number
  glossaryKey: string
  accent?: boolean
}) {
  const barColor = accent ? '#C8761A' : scoreBarColor(value)
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1.5">
        <span className="flex items-center gap-1 text-sm text-neutral-500">
          {label} <InfoTooltip term={glossaryKey} />
        </span>
        <span className="text-sm font-medium" style={{ color: accent ? '#C8761A' : scoreColor(value) }}>
          {Math.round(value)}
        </span>
      </div>
      <div className="h-1.5 bg-neutral-100 rounded-full">
        <div
          className="h-1.5 rounded-full transition-all duration-500"
          style={{ width: `${Math.min(value, 100)}%`, background: barColor }}
        />
      </div>
    </div>
  )
}

// ─── Metric row ───────────────────────────────────────────────────────────────

function MetricRow({ label, value, glossaryKey, tag }: {
  label: string
  value: string
  glossaryKey: string
  tag?: TagConfig
}) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-neutral-50 last:border-0">
      <span className="text-sm text-neutral-500 flex items-center gap-1">
        {label} <InfoTooltip term={glossaryKey} />
      </span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{value}</span>
        {tag && <Tag {...tag} />}
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function SuburbIntelligenceSection({ property, htagData: externalData, htagLoading: externalLoading, htagError: externalError }: SuburbIntelligenceSectionProps) {
  // Internal fetch state — only used when parent doesn't supply data
  const [internalHtag, setInternalHtag] = useState<HtagSummary | null>(null)
  const [internalLoading, setInternalLoading] = useState(true)
  const [internalError, setInternalError] = useState<string | null>(null)
  const [aiSignal, setAiSignal] = useState<string | null>(null)

  const parentManaged = externalData !== undefined || externalLoading !== undefined || externalError !== undefined
  const htag = parentManaged ? (externalData ?? null) : internalHtag
  const loading = parentManaged ? (externalLoading ?? false) : internalLoading
  const error = parentManaged ? (externalError ?? null) : internalError

  const rawType = property.propertyType ?? property.assetClass ?? 'house'
  const typeConfig = getPropertyTypeConfig(rawType)
  const htagType = getHtagType(rawType) ?? 'house'
  const propertyTypeLabel = htagType === 'unit' ? 'Units' : 'Houses'

  // Map camelCase PropertyRecord → PropertyMapPanel shape
  const mapProperty = {
    address: property.address ?? '',
    suburb: property.suburb ?? '',
    state: property.state ?? '',
    postcode: property.postcode ?? '',
    latitude: property.latitude ?? null,
    longitude: property.longitude ?? null,
    property_type: rawType,
  }

  // Internal fetch — only when parent doesn't manage data (backwards compat)
  useEffect(() => {
    if (parentManaged) return
    if (!property.lgaId || typeConfig.availability === 'none') {
      setInternalLoading(false)
      return
    }

    setInternalLoading(true)
    setInternalError(null)

    supabase!.functions.invoke('htag-data', {
      body: {
        lga_id: property.lgaId,
        property_type: htagType,
        ...(property.addressKey ? { address_key: property.addressKey } : {}),
      },
    })
      .then(res => {
        if (res.error) throw new Error(res.error.message)
        setInternalHtag(res.data as HtagSummary)
      })
      .catch(e => setInternalError(String((e as Error)?.message ?? 'Failed to load market data')))
      .finally(() => setInternalLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property.lgaId, htagType, parentManaged])

  // Build financials for AI — use optional chaining throughout
  const financials: PropertyFinancialContext = {
    purchase_price: property.purchasePrice ?? 0,
    purchase_date: property.purchaseDate ?? '',
    current_avm: htag?.estimate?.estimate_mid ?? null,
    weekly_rent: property.weeklyRent ?? null,
    gross_yield: null,
    lvr: null,
    equity: null,
  }

  // ── CASE 1: No HTAG data for this property type ───────────────────────────
  if (typeConfig.availability === 'none') {
    return (
      <div className="space-y-4">
        <PropertyMapPanel
          property={mapProperty}
          cycle_phase={null}
          investment_signal={null}
        />
        <div className="rounded-2xl border border-neutral-100 bg-white p-6">
          <div className="flex items-center gap-3 mb-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
            </svg>
            <span className="text-sm font-medium text-neutral-600">Market intelligence not available</span>
          </div>
          <p className="text-sm text-neutral-400 leading-relaxed">
            HTAG market data is available for residential properties (houses, units, townhouses).
            Commercial and non-residential properties show location context only.
          </p>
        </div>
      </div>
    )
  }

  // ── CASE 2: lga_id missing ────────────────────────────────────────────────
  if (!property.lgaId) {
    return (
      <div className="space-y-4">
        <PropertyMapPanel
          property={mapProperty}
          cycle_phase={null}
          investment_signal={null}
        />
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <p className="text-sm text-amber-800">
            Market intelligence requires suburb data. Re-import this property to enable HTAG analytics.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* ── Map panel — always render ── */}
      <PropertyMapPanel
        property={mapProperty}
        avm_low={htag?.estimate?.estimate_low ?? null}
        avm_mid={htag?.estimate?.estimate_mid ?? null}
        avm_high={htag?.estimate?.estimate_high ?? null}
        cycle_phase={htag?.cycle?.phase ?? null}
        investment_signal={aiSignal}
      />

      {/* ── Disclaimer for partial data (duplex / townhouse) ── */}
      {typeConfig.disclaimer && typeConfig.availability === 'partial' && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
          <p className="text-xs text-amber-800">⚠ {typeConfig.disclaimer}</p>
        </div>
      )}

      {/* ── Loading state ── */}
      {loading && (
        <div className="animate-pulse space-y-3">
          <div className="h-32 bg-neutral-100 rounded-2xl" />
          <div className="h-52 bg-neutral-100 rounded-2xl" />
          <div className="h-40 bg-neutral-100 rounded-2xl" />
        </div>
      )}

      {/* ── Error state ── */}
      {error && !htag && !loading && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <p className="text-sm text-amber-800">{error}</p>
        </div>
      )}

      {/* ── HTAG data loaded ── */}
      {htag && !loading && (
        <>
          {/* Data caption */}
          <p className="text-xs text-neutral-400 pt-2">
            {htag.meta.lga_name} · {propertyTypeLabel} · Data as of {fmt.date(htag.meta.period_end)}
          </p>

          {/* A — AI summary */}
          <MarketInsightSummary
            htag_summary={htag}
            property_financials={financials}
            property_type_label={propertyTypeLabel}
            lga_name={htag.meta.lga_name}
            onSignalResolved={(signal) => setAiSignal(signal)}
          />

          {/* A2 — LGA Market Overview (derived from price trends) */}
          {(htag.summary?.typical_price ?? 0) > 0 && (() => {
            const ms = htag.summary!
            return (
              <div className="rounded-2xl border border-neutral-100 bg-white p-6">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-neutral-800 uppercase tracking-wider">
                    LGA Market Overview
                  </h3>
                  <p className="text-xs text-neutral-400 mt-0.5">
                    Benchmarks for {propertyTypeLabel} in {htag.meta.lga_name}
                  </p>
                </div>
                <div className={`grid gap-4 ${ms.weekly_rent > 0 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1'}`}>
                  <div>
                    <div className="text-xs text-neutral-400">LGA typical price</div>
                    <div className="text-lg font-semibold text-neutral-900">{fmt.currency(ms.typical_price)}</div>
                  </div>
                  {ms.weekly_rent > 0 && (
                    <div>
                      <div className="text-xs text-neutral-400">LGA typical rent</div>
                      <div className="text-lg font-semibold text-neutral-900">${Math.round(ms.weekly_rent)}/wk</div>
                    </div>
                  )}
                  {ms.gross_yield > 0 && (
                    <div>
                      <div className="text-xs text-neutral-400">LGA gross yield</div>
                      <div className="text-lg font-semibold text-neutral-900">{(ms.gross_yield * 100).toFixed(1)}%</div>
                    </div>
                  )}
                  {ms.annual_sales > 0 && (
                    <div>
                      <div className="text-xs text-neutral-400">Annual sales</div>
                      <div className="text-lg font-semibold text-neutral-900">{Math.round(ms.annual_sales)}/yr</div>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* B — RCS scores card */}
          <div className="rounded-2xl border border-neutral-100 bg-white p-6">
            <div className="flex items-center gap-2 mb-5">
              <h3 className="text-sm font-semibold text-neutral-800 uppercase tracking-wider">
                RCS™ Market Scores
              </h3>
              <InfoTooltip term="rcs_overall" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left — score bars */}
              <div>
                <ScoreBar label="Overall" value={htag.scores.overall} glossaryKey="rcs_overall" accent />
                <p className="text-xs text-neutral-400 -mt-2 mb-4">
                  {Math.round(htag.scores.overall)}/100 — {rcsOverallLabel(htag.scores.overall)}
                </p>
                <ScoreBar label="Capital Growth" value={htag.scores.capital_growth} glossaryKey="rcs_capital_growth" />
                <ScoreBar label="Cashflow" value={htag.scores.cashflow} glossaryKey="rcs_cashflow" />
                <ScoreBar label="Lower Risk" value={htag.scores.lower_risk} glossaryKey="rcs_lower_risk" />
              </div>

              {/* Right — cycle position */}
              <div>
                <div className="text-xs text-neutral-500 font-semibold uppercase tracking-wider mb-3">
                  Growth Rate Cycle <InfoTooltip term="growth_rate_cycle" />
                </div>
                {/* 4-stage visual track */}
                <div className="grid grid-cols-4 gap-1 mb-3">
                  {(['Bottom', 'Rising', 'Peak', 'Falling'] as const).map((stage) => {
                    const isActive = htag.cycle.phase.toLowerCase().includes(stage.toLowerCase())
                    return (
                      <div
                        key={stage}
                        className={`text-center py-2 px-1 rounded text-xs font-medium transition-all ${
                          isActive
                            ? stage === 'Peak' ? 'bg-amber-100 text-amber-800 ring-2 ring-amber-400'
                            : stage === 'Rising' ? 'bg-green-100 text-green-800 ring-2 ring-green-400'
                            : stage === 'Falling' ? 'bg-red-100 text-red-800 ring-2 ring-red-400'
                            : 'bg-blue-100 text-blue-800 ring-2 ring-blue-400'
                            : 'bg-neutral-50 text-neutral-400'
                        }`}
                      >
                        {stage}
                        {isActive && <div className="text-xs mt-0.5 opacity-75">▲ now</div>}
                      </div>
                    )
                  })}
                </div>
                <p className="text-xs text-neutral-500 mb-3">{getCycleExplanation(htag.cycle.phase)}</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-400 flex items-center gap-1">
                      Projected growth <InfoTooltip term="growth_low" />
                    </span>
                    <span>
                      <span className="text-red-600 font-medium">{fmt.cagr(htag.cycle.growth_low)}</span>
                      {' – '}
                      <span className="text-green-600 font-medium">{fmt.cagr(htag.cycle.growth_high)}</span>
                      <span className="text-neutral-400"> p.a.</span>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400 flex items-center gap-1">
                      Rent growth <InfoTooltip term="growth_low" />
                    </span>
                    <span className="font-medium">{fmt.cagr(htag.cycle.rent_growth)} p.a.</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-neutral-100">
                  <div>
                    <div className="text-xs text-neutral-400 mb-1 flex items-center gap-1">
                      HAPI <InfoTooltip term="hapi" />
                    </div>
                    <div className="text-lg font-medium text-neutral-800">
                      {htag.scores.hapi.toFixed(1)}<span className="text-xs text-neutral-400">/10</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-400 mb-1 flex items-center gap-1">
                      Volatility <InfoTooltip term="volatility" />
                    </div>
                    <div className="text-lg font-medium text-neutral-800">
                      {htag.scores.volatility.toFixed(1)}<span className="text-xs text-neutral-400">/10</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* C — Growth chart card */}
          {htag.trends.length > 0 && (
            <div className="rounded-2xl border border-neutral-100 bg-white p-6">
              <div className="mb-1">
                <h3 className="text-sm font-semibold text-neutral-800 uppercase tracking-wider">
                  Annual Growth Rate
                </h3>
                <p className="text-xs text-neutral-400 mt-0.5">
                  {htag.meta.lga_name} · {propertyTypeLabel}
                </p>
              </div>

              <IntelligenceChart trends={htag.trends} highlight_year="2022" />

              {/* CAGR grid */}
              <div className="border-t border-neutral-100 pt-4 mt-2">
                <div className="grid grid-cols-5 gap-2 text-center">
                  <div className="text-sm font-medium text-gray-600 text-left flex items-center gap-1">Price <InfoTooltip term="cagr_price" /></div>
                  {([
                    { v: htag.growth.price_1y, label: '1yr' },
                    { v: htag.growth.price_3y, label: '3yr' },
                    { v: htag.growth.price_5y, label: '5yr' },
                    { v: htag.growth.price_10y, label: '10yr' },
                  ] as { v: number; label: string }[]).map(({ v, label }) => (
                    <div key={label}>
                      <div className={`text-base font-medium ${cagrColor(v)}`}>{fmt.cagr(v)}</div>
                      <div className="text-xs text-neutral-400">{label}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-5 gap-2 text-center mt-2">
                  <div className="text-sm font-medium text-gray-600 text-left flex items-center gap-1">Rent <InfoTooltip term="cagr_rent" /></div>
                  {([
                    { v: htag.growth.rent_1y, label: '1yr' },
                    { v: htag.growth.rent_3y, label: '3yr' },
                    { v: htag.growth.rent_5y, label: '5yr' },
                    { v: htag.growth.rent_10y, label: '10yr' },
                  ] as { v: number; label: string }[]).map(({ v, label }) => (
                    <div key={label}>
                      <div className={`text-base font-medium ${cagrColor(v)}`}>{fmt.cagr(v)}</div>
                      <div className="text-xs text-neutral-400">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* D — Demand & Supply card */}
          <div className="rounded-2xl border border-neutral-100 bg-white p-6">
            <h3 className="text-sm font-semibold text-neutral-800 uppercase tracking-wider mb-4">
              Demand &amp; Supply
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs text-neutral-500 font-semibold uppercase tracking-wider mb-2">Demand</div>
                <MetricRow label="Days on market" value={`${Math.round(htag.demand.dom)} days`} glossaryKey="dom" tag={domTag(htag.demand.dom)} />
                <MetricRow label="Clearance rate" value={fmt.pct(htag.demand.clearance_rate)} glossaryKey="clearance_rate" />
                <MetricRow label="Vacancy rate" value={fmt.pct(htag.demand.vacancy_rate)} glossaryKey="vacancy_rate" tag={vacancyTag(htag.demand.vacancy_rate)} />
                <MetricRow label="Vendor discounting" value={fmt.pct(htag.demand.discounting)} glossaryKey="discounting" />
                <MetricRow label="Buy search interest" value={`${htag.demand.buy_search_interest} / 10`} glossaryKey="buy_search_interest" />
                <MetricRow label="Rent search interest" value={`${htag.demand.rent_search_interest} / 10`} glossaryKey="rent_search_interest" />
              </div>
              <div>
                <div className="text-xs text-neutral-500 font-semibold uppercase tracking-wider mb-2">Supply</div>
                <MetricRow label="Stock on market" value={fmt.pct(htag.supply.stock_on_market)} glossaryKey="stock_on_market" />
                <MetricRow label="Inventory" value={`${htag.supply.inventory.toFixed(1)} months`} glossaryKey="inventory" tag={inventoryTag(htag.supply.inventory)} />
                <MetricRow label="Hold period" value={fmt.yr(htag.supply.hold_period)} glossaryKey="hold_period" />
                <MetricRow label="Building approvals" value={htag.supply.building_approvals.toLocaleString()} glossaryKey="building_approvals" />
                <MetricRow label="BA ratio" value={fmt.pct(htag.supply.ba_ratio)} glossaryKey="ba_ratio" />
              </div>
            </div>
          </div>

          {/* E — Fundamentals & Risk card */}
          <div className="rounded-2xl border border-neutral-100 bg-white p-6">
            <h3 className="text-sm font-semibold text-neutral-800 uppercase tracking-wider mb-4">
              Fundamentals &amp; Risk
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs text-neutral-500 font-semibold uppercase tracking-wider mb-2">Fundamentals</div>
                <MetricRow label="IRSAD decile" value={`${htag.fundamentals.irsad} / 10`} glossaryKey="irsad" />
                <MetricRow label="Renter ratio" value={fmt.pctInt(htag.fundamentals.renter_ratio)} glossaryKey="renter_ratio" />
                <MetricRow label="Years to own" value={fmt.yr(htag.fundamentals.years_to_own)} glossaryKey="years_to_own" />
                <MetricRow label="Distance to GPO" value={fmt.km(htag.fundamentals.gpo_dist)} glossaryKey="gpo_dist" />
              </div>
              <div>
                <div className="text-xs text-neutral-500 font-semibold uppercase tracking-wider mb-2">Risk</div>
                <MetricRow label="Flood risk" value={`${htag.risk.flood_percentile}th pctile`} glossaryKey="flood_percentile" tag={riskTag(htag.risk.flood_percentile)} />
                <MetricRow label="Fire risk" value={`${htag.risk.fire_percentile}th pctile`} glossaryKey="fire_percentile" tag={riskTag(htag.risk.fire_percentile)} />
                <MetricRow label="Economic diversity" value={`${htag.risk.edi} / 100`} glossaryKey="edi" />
                <MetricRow label="Market activity" value={`${htag.risk.madi} / 100`} glossaryKey="madi" />
              </div>
            </div>
          </div>

          {/* F — AVM estimate card */}
          {htag.estimate && (
            <div className="rounded-2xl border border-neutral-100 bg-white p-6">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-neutral-800 uppercase tracking-wider">
                  Property Estimate
                </h3>
                <InfoTooltip term="avm_estimate" />
              </div>
              <p className="text-xs text-neutral-400 mb-4">
                Automated Valuation Model — treat as directional guide ±10%
              </p>

              <div className="grid grid-cols-3 divide-x divide-neutral-100 my-4">
                <div className="text-center px-4">
                  <div className="text-xs text-neutral-400 mb-1">Low</div>
                  <div className="text-base font-medium text-neutral-500">
                    {htag.estimate.estimate_low ? fmt.currency(htag.estimate.estimate_low) : '—'}
                  </div>
                </div>
                <div className="text-center px-4">
                  <div className="text-xs text-neutral-400 mb-1">Estimate</div>
                  <div className="text-xl font-medium" style={{ color: '#C8761A' }}>
                    {htag.estimate.estimate_mid ? fmt.currency(htag.estimate.estimate_mid) : '—'}
                  </div>
                </div>
                <div className="text-center px-4">
                  <div className="text-xs text-neutral-400 mb-1">High</div>
                  <div className="text-base font-medium text-neutral-500">
                    {htag.estimate.estimate_high ? fmt.currency(htag.estimate.estimate_high) : '—'}
                  </div>
                </div>
              </div>

              {htag.estimate.last_sale_price && (
                <p className="text-xs text-neutral-400 mt-2">
                  Last sale: {fmt.currency(htag.estimate.last_sale_price)}
                  {htag.estimate.last_sale_date ? ` · ${fmt.date(htag.estimate.last_sale_date)}` : ''}
                </p>
              )}

              <div className="flex items-center gap-1 mt-3">
                <p className="text-xs text-neutral-400 italic">
                  Confidence range <InfoTooltip term="avm_range" />
                </p>
              </div>
            </div>
          )}

          {/* G — Data footer */}
          <div className="flex flex-col items-center gap-2 pt-4 pb-2">
            <a
              href="https://developer.htagai.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#1A6BF3]/30 bg-[#1A6BF3]/5 hover:bg-[#1A6BF3]/10 transition-colors"
            >
              <span className="text-xs text-neutral-500">Powered by</span>
              <span className="text-xs font-semibold text-[#1A6BF3]">HtAG Analytics</span>
            </a>
            <p className="text-xs text-neutral-400">LGA-level data · {propertyTypeLabel} · Refreshed monthly</p>
            <p className="text-center text-xs text-neutral-300 px-4">
              Market intelligence data is provided for informational purposes only and does not constitute financial, legal, or property valuation advice.
            </p>
          </div>
        </>
      )}
    </div>
  )
}

export default SuburbIntelligenceSection
